import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Brain, Zap, Grid, Timer, Play, Trophy, Crown, Medal
} from "lucide-react";
import { toast } from "sonner";

// Game sub-components
import MemorySequenceGame from "./games/MemorySequenceGame";
import ReactionTestGame from "./games/ReactionTestGame";
import MathSprintGame from "./games/MathSprintGame";
import PatternMatchGame from "./games/PatternMatchGame";
import WordRecallGame from "./games/WordRecallGame";
import { devError } from "../utils/devLogger";

const GAME_ICON_MAP = { memory: Brain, focus: Zap };
const GAME_NAMES = {
  memory_sequence: "Memory Sequence",
  reaction_test: "Reaction Test",
  math_sprint: "Math Sprint",
  pattern_match: "Classic Memory",
  word_recall: "Word Recall",
};

// Leaderboard sub-component
const Leaderboard = ({ t }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [filterGame, setFilterGame] = useState("");
  const [filterDiff, setFilterDiff] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterGame) params.append("game_id", filterGame);
      if (filterDiff) params.append("difficulty", filterDiff);
      const res = await axios.get(`${API}/games/leaderboard?${params.toString()}`);
      setLeaderboard(res.data.leaderboard);
      setMyRank(res.data.my_rank);
    } catch (error) {
      devError("Leaderboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [filterGame, filterDiff]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-amber-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-orange-400" />;
    return <span className="text-sm text-slate-400 font-medium w-5 text-center">{rank}</span>;
  };

  return (
    <div data-testid="leaderboard-section">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <select
          value={filterGame}
          onChange={(e) => setFilterGame(e.target.value)}
          className="text-sm px-3 py-1.5 rounded-lg border border-sky-200 bg-white text-sky-800 outline-none focus:ring-2 focus:ring-sky-300"
          data-testid="leaderboard-game-filter"
        >
          <option value="">All Games</option>
          {Object.entries(GAME_NAMES).map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <select
          value={filterDiff}
          onChange={(e) => setFilterDiff(e.target.value)}
          className="text-sm px-3 py-1.5 rounded-lg border border-sky-200 bg-white text-sky-800 outline-none focus:ring-2 focus:ring-sky-300"
          data-testid="leaderboard-diff-filter"
        >
          <option value="">All Difficulties</option>
          <option value="easy">{t("games.difficulty.easy")}</option>
          <option value="medium">{t("games.difficulty.medium")}</option>
          <option value="hard">{t("games.difficulty.hard")}</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 rounded-full bg-sky-400/30 glow-pulse mx-auto" />
        </div>
      ) : leaderboard.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No scores yet. Be the first!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leaderboard.map((entry) => (
            <div
              key={`${entry.user_id}-${entry.game_id}`}
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                entry.is_you
                  ? "bg-sky-100 border border-sky-300"
                  : "bg-white/60 border border-transparent hover:border-sky-100"
              }`}
              data-testid={`leaderboard-entry-${entry.rank}`}
            >
              <div className="w-8 flex justify-center">
                {getRankIcon(entry.rank)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${entry.is_you ? "text-sky-700" : "text-slate-700"}`}>
                  {entry.name} {entry.is_you && <span className="text-xs text-sky-500">(you)</span>}
                </p>
                <p className="text-xs text-slate-400">
                  {GAME_NAMES[entry.game_id] || entry.game_id} &middot; Lvl {entry.best_level} &middot; {entry.games_played} played
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
                  {entry.best_score}
                </p>
              </div>
            </div>
          ))}

          {myRank && myRank > 50 && (
            <div className="text-center text-sm text-slate-400 pt-2">
              Your rank: #{myRank}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CognitiveGames = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("games"); // "games" | "leaderboard"

  const fetchGames = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games`);
      setGames(response.data.games);
    } catch (error) {
      devError("Failed to fetch games:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games/history`);
      setHistory(response.data);
    } catch (error) {
      devError("Failed to fetch history:", error);
    }
  }, []);

  useEffect(() => {
    fetchGames();
    fetchHistory();
  }, [fetchGames, fetchHistory]);

  const handleGameComplete = useCallback(async (score, level, accuracy = null, difficulty = "medium") => {
    try {
      const response = await axios.post(`${API}/games/result`, {
        game_id: activeGame,
        score,
        level,
        time_taken_seconds: 60,
        accuracy,
        difficulty,
      });

      if (response.data.is_personal_best) {
        toast.success(`${t("games.newPersonalBest")}: ${score}!`, {
          duration: 4000,
          description: "You beat your previous record!",
        });
      } else {
        toast.success(`${t("common.score")}: ${score}`);
      }

      const { new_records } = response.data;
      if (new_records?.length > 0) {
        new_records.forEach((record) => {
          if (record.type === "streak_record") {
            toast("New Streak Record!", {
              duration: 5000,
              description: `${record.value}-day streak! Your longest ever!`,
            });
          }
        });
      }

      fetchHistory();
    } catch (error) {
      toast.error("Failed to save result");
    }
  }, [activeGame, fetchHistory, t]);

  const renderGame = () => {
    const props = {
      onComplete: handleGameComplete,
      onBack: () => setActiveGame(null),
      t,
    };

    switch (activeGame) {
      case "memory_sequence": return <MemorySequenceGame {...props} />;
      case "reaction_test":   return <ReactionTestGame {...props} />;
      case "math_sprint":     return <MathSprintGame {...props} />;
      case "pattern_match":   return <PatternMatchGame {...props} />;
      case "word_recall":     return <WordRecallGame {...props} />;
      default:
        return (
          <div className="text-center py-12">
            <p className="text-slate-500 mb-4">Game coming soon!</p>
            <Button onClick={() => setActiveGame(null)} className="glow-button">{t("games.backToGames")}</Button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="cognitive-games-page">
      <div className="fixed top-0 left-0 w-96 h-96 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => (activeGame ? setActiveGame(null) : navigate("/dashboard"))}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {activeGame ? t("games.title") : t("common.dashboard")}
            </Button>
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-indigo-500" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t("games.title")}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          {!activeGame ? (
            <>
              {/* Stats bar */}
              {history.stats && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="glass-card p-4 text-center" data-testid="stat-games-played">
                    <p className="text-2xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
                      {history.stats.games_played}
                    </p>
                    <p className="text-xs text-slate-500">{t("games.gamesPlayed")}</p>
                  </div>
                  <div className="glass-card p-4 text-center" data-testid="stat-best-score">
                    <p className="text-2xl font-light text-amber-500" style={{ fontFamily: "Outfit" }}>
                      {history.stats.best_score}
                    </p>
                    <p className="text-xs text-slate-500">{t("games.bestScore")}</p>
                  </div>
                  <div className="glass-card p-4 text-center" data-testid="stat-average">
                    <p className="text-2xl font-light text-indigo-500" style={{ fontFamily: "Outfit" }}>
                      {history.stats.average_score}
                    </p>
                    <p className="text-xs text-slate-500">{t("games.average")}</p>
                  </div>
                </div>
              )}

              {/* Tab switcher */}
              <div className="flex gap-1 mb-6 bg-white/60 p-1 rounded-xl w-fit" data-testid="games-tabs">
                <button
                  onClick={() => setTab("games")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    tab === "games"
                      ? "bg-sky-500 text-white shadow-sm"
                      : "text-slate-500 hover:text-sky-700"
                  }`}
                  data-testid="tab-games"
                >
                  <Play className="w-4 h-4 inline mr-1.5" />
                  Games
                </button>
                <button
                  onClick={() => setTab("leaderboard")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    tab === "leaderboard"
                      ? "bg-sky-500 text-white shadow-sm"
                      : "text-slate-500 hover:text-sky-700"
                  }`}
                  data-testid="tab-leaderboard"
                >
                  <Trophy className="w-4 h-4 inline mr-1.5" />
                  Leaderboard
                </button>
              </div>

              {tab === "games" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="games-grid">
                  {games.map((game) => {
                    const Icon = GAME_ICON_MAP[game.type] || Grid;
                    return (
                      <div
                        key={game.id}
                        className="glass-card glass-card-hover p-6 cursor-pointer group"
                        onClick={() => { setActiveGame(game.id); setTab("games"); }}
                        data-testid={`game-${game.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Icon className="w-7 h-7 text-indigo-500" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-sky-800">{game.name}</h3>
                            <p className="text-sm text-slate-500 mt-1">{game.description}</p>
                            <div className="flex items-center gap-3 mt-3">
                              <span className="text-xs text-slate-400">
                                <Timer className="w-3 h-3 inline mr-1" />
                                {game.duration_minutes} {t("common.minutes")}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600">
                                {game.type}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-600">
                                3 {t("games.levels")}
                              </span>
                            </div>
                          </div>
                          <Play className="w-8 h-8 text-sky-400 group-hover:text-sky-600 transition-colors" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="glass-card p-6">
                  <Leaderboard t={t} />
                </div>
              )}
            </>
          ) : (
            <div className="glass-card p-8" data-testid="active-game-container">
              {renderGame()}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CognitiveGames;
